import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api, extractError } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import NotificationSettings from "../components/NotificationSettings";

const ROLE_LABEL = { admin: "Admin", accounting: "Accounting", worker: "Worker" };

function Avatar({ user, size = 64 }) {
  const initials = (() => {
    const name = (user?.name || "").trim();
    if (name) {
      const parts = name.split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    }
    return ((user?.email || "??").split("@")[0]).slice(0, 2).toUpperCase();
  })();
  return (
    <div style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      className="flex items-center justify-center rounded-full bg-[#0F1E3D] font-semibold text-white">
      {initials}
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div className="flex items-baseline justify-between border-b border-slate-100 py-2 last:border-0">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900" data-testid={testId}>{children}</dd>
    </div>
  );
}

/* ----------------------------- ChangePasswordDialog ----------------------------- */

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errs, setErrs] = useState({}); // {current?, new?, confirm?, general?}
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setCur(""); setNext(""); setConfirm(""); setErrs({}); setSubmitting(false);
    }
  }, [open]);

  const validate = () => {
    const e = {};
    if (!cur) e.current = "Required";
    if (!next) e.new = "Required";
    else if (next.length < 8) e.new = "Must be at least 8 characters";
    else if (!/[a-zA-Z]/.test(next) || !/\d/.test(next)) e.new = "Must contain a letter and a digit";
    else if (cur && next === cur) e.new = "New password must differ from current";
    if (!confirm) e.confirm = "Required";
    else if (confirm !== next) e.confirm = "Does not match new password";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrs(e);
    if (Object.keys(e).length > 0) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      onOpenChange(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = extractError(err);
      if (status === 401) {
        setErrs({ current: "Current password is incorrect" });
      } else if (status === 422) {
        // Server-side strength rejection — surface on the new-password field.
        setErrs({ new: detail || "Password does not meet requirements" });
      } else {
        setErrs({ general: detail || "Failed to change password" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>
            Use a strong password — at least 8 characters, mixing letters and digits.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {errs.general && (
            <Alert variant="destructive" data-testid="change-password-general-error">
              <AlertDescription>{errs.general}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              data-testid="change-password-current"
              aria-invalid={!!errs.current}
            />
            {errs.current && (
              <p className="text-xs text-rose-600" data-testid="change-password-current-error">{errs.current}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="change-password-new"
              aria-invalid={!!errs.new}
            />
            {errs.new && (
              <p className="text-xs text-rose-600" data-testid="change-password-new-error">{errs.new}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="change-password-confirm"
              aria-invalid={!!errs.confirm}
            />
            {errs.confirm && (
              <p className="text-xs text-rose-600" data-testid="change-password-confirm-error">{errs.confirm}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="change-password-submit"
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
              {submitting ? "Changing…" : "Change password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------- Account --------------------------------- */

export default function Account() {
  const { user } = useAuth();
  const [dept, setDept] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    if (!user?.default_department_id) { setDept(null); return; }
    api.get("/departments/active")
      .then((r) => {
        const d = (r.data || []).find((x) => x.id === user.default_department_id);
        setDept(d || null);
      })
      .catch(() => setDept(null));
  }, [user]);

  if (!user) return null;

  return (
    <div className="mx-auto max-w-2xl" data-testid="account-page">
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Account</h1>

      <Card className="mb-6 border-slate-200">
        <CardContent className="flex items-center gap-4 pt-6">
          <Avatar user={user} />
          <div>
            <p className="font-semibold text-slate-900" data-testid="account-name">{user.name}</p>
            <p className="text-sm text-slate-500" data-testid="account-email">{user.email}</p>
            <Badge variant="secondary" className="mt-1 bg-[#0F1E3D]/5 text-[#0F1E3D]" data-testid="account-role">
              <ShieldCheck className="mr-1 h-3 w-3" />{ROLE_LABEL[user.role] || user.role}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6 border-slate-200">
        <CardContent className="pt-6">
          <h2 className="mb-2 text-sm font-semibold text-slate-700">Profile</h2>
          <dl>
            <Field label="Name" testId="account-field-name">{user.name}</Field>
            <Field label="Email" testId="account-field-email">{user.email}</Field>
            <Field label="Role" testId="account-field-role">{ROLE_LABEL[user.role] || user.role}</Field>
            <Field label="Default department" testId="account-field-dept">
              {dept ? `${dept.name} (${dept.code})` : (user.default_department_id ? "—" : "No default")}
            </Field>
          </dl>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Security</h2>
          <Button
            variant="outline"
            onClick={() => setPwOpen(true)}
            data-testid="change-password-btn">
            <Lock className="mr-2 h-4 w-4" /> Change password
          </Button>
          <p className="mt-2 text-xs text-slate-500">Pick something only you would type.</p>
        </CardContent>
      </Card>

      <NotificationSettings />

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
